import { Comment as CommentDto } from '@/actions/dtos/tender/comment';
import TimeAgo from 'timeago-react';
import '@/lib/timeago/custom-de-locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlaneTop, faTrashCan } from '@fortawesome/pro-duotone-svg-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAccount } from '@/hooks/useAccount';
import { MemberRole } from '@/actions/dtos/organisation';

interface CommentProps {
  comment: CommentDto;
  handleDeleteComment: (id: string) => void;
  handleEditComment: (id: string, newMessage: string) => void;
}

const EMPTY_COMMENT_ERROR_MESSAGE = 'Kommentar darf nicht leer sein';
const MAX_COMMENT_LENGTH = 2000;

export const Comment: React.FC<CommentProps> = ({ comment, handleDeleteComment, handleEditComment }) => {
  const [editedComment, setEditedComment] = useState<string>(comment.message);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { person, checkPermission } = useAccount();
  const isAuthor = person?.id === comment.author.id;
  const canDeleteComment = checkPermission([MemberRole.ADMIN, MemberRole.OWNER]) || isAuthor;

  const toggleCommentMode = () => {
    if (isAuthor) {
      setIsEditMode(true);
    }
  };

  const handleOnInputChange = (editedComment: string) => {
    if (editedComment.trim().length <= MAX_COMMENT_LENGTH) {
      setEditedComment(editedComment);
      if (error) setError(null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      saveComment();
    }
  };

  const saveComment = useCallback(() => {
    if (editedComment.trim() === '') {
      setError(EMPTY_COMMENT_ERROR_MESSAGE);
      return;
    }
    handleEditComment(comment.id, editedComment.trim());
    setIsEditMode(false);
  }, [comment.id, editedComment, handleEditComment]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditMode && textareaRef.current && !textareaRef.current.contains(event.target as Node)) {
        saveComment();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditMode, editedComment, saveComment]);

  return (
    <div className="flex flex-col gap-2 rounded-lg border-primary bg-base-200 py-2">
      <span className="flex justify-between px-2">
        <p className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold">
          {comment.author.firstname}
        </p>
        <span className="flex items-center gap-2">
          <TimeAgo
            className="text-xs text-primary opacity-50"
            datetime={comment.created}
            locale="de-custom"
            opts={{ minInterval: 60 }}
          />
          {canDeleteComment && !isEditMode && (
            <button onClick={() => handleDeleteComment(comment.id)}>
              <FontAwesomeIcon className="fa-fw h-4 w-4" icon={faTrashCan} />
            </button>
          )}
          {isEditMode && (
            <button onClick={saveComment} disabled={editedComment.trim() === ''}>
              <FontAwesomeIcon className={`fa-fw h-4 w-4`} icon={faPaperPlaneTop} />
            </button>
          )}
        </span>
      </span>
      {!isEditMode ? (
        <p
          onClick={toggleCommentMode}
          className={`${isAuthor ? 'cursor-pointer' : ''} whitespace-normal break-words px-2`}
        >
          {comment.message}
        </p>
      ) : (
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={editedComment}
            onChange={(e) => handleOnInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`resize-none bg-base-200 focus:outline-none ${error ? 'mx-4 rounded-lg border border-error' : 'w-full border-none px-2'}`}
            autoFocus
            rows={2}
          />
          {error && <p className="px-4 text-sm text-error">{error}</p>}
        </div>
      )}
    </div>
  );
};
